import { z } from "zod";
/**
 * Platform error reporting shared by every OpenCloud surface. Server sources
 * are recorded by trusted platform code; client sources are self-reported by a
 * browser or the public CLI and are labelled as such.
 */
export const PLATFORM_ERROR_SOURCES = [
    "api",
    "worker",
    "mcp",
    "agent",
    "agent_ui",
    "dashboard",
    "admin",
    "web",
    "cli",
    "alert",
];
export const platformErrorSourceSchema = z.enum(PLATFORM_ERROR_SOURCES);
/** Sources a client may claim on the public report endpoint. */
export const PLATFORM_ERROR_CLIENT_SOURCES = [
    "dashboard",
    "admin",
    "agent_ui",
    "web",
    "cli",
];
export const PLATFORM_ERROR_KINDS = [
    /** An unexpected exception in platform server code. */
    "exception",
    /** A platform server answered with a 5xx status. */
    "http_error",
    /** A structured error-level log event from platform server code. */
    "log_error",
    /** An uncaught exception or unhandled rejection that stops the service. */
    "process_crash",
    /** A durable operation ended failed for a reason that is not the app's. */
    "operation_failed",
    /** A platform background job failed. */
    "job_failed",
    /** A hosted Agent run ended failed for a platform reason. */
    "agent_run_failed",
    /** A Prometheus platform alert fired. */
    "alert",
    /** A hosted MCP tool call failed inside the platform. */
    "mcp_tool_failed",
    /** A browser runtime or render error. */
    "client_error",
    /** A browser could not load application code, usually after a release. */
    "chunk_load_failed",
    /** A client saw an API 5xx answer or no answer at all. */
    "api_error",
    /** A public CLI command failed unexpectedly. */
    "cli_error",
    /** The public CLI crashed. */
    "cli_crash",
    /** A person reported a problem. */
    "report",
];
export const platformErrorKindSchema = z.enum(PLATFORM_ERROR_KINDS);
export const PLATFORM_ERROR_CLIENT_KINDS = [
    "client_error",
    "chunk_load_failed",
    "api_error",
    "cli_error",
    "cli_crash",
    "report",
];
/** Issue workflow: resolve once a fix is live; a resolved issue reopens when it recurs. */
export const PLATFORM_ISSUE_STATUSES = [
    "new",
    "in_progress",
    "resolved",
    "wont_fix",
];
export const platformIssueStatusSchema = z.enum(PLATFORM_ISSUE_STATUSES);
export const PLATFORM_ISSUE_SEVERITIES = ["warning", "error", "critical"];
export const platformIssueSeveritySchema = z.enum(PLATFORM_ISSUE_SEVERITIES);
export const PLATFORM_ERROR_LIMITS = {
    message: 2_000,
    stack: 16_000,
    title: 300,
    route: 300,
    url: 2_000,
    contextKeys: 40,
    contextString: 500,
    clientReports: 20,
    internalEvents: 50,
};
const requestIdSchema = z.string().regex(/^[A-Za-z0-9._:-]{1,128}$/);
const shortText = (max) => z.string().max(max);
export const platformErrorContextSchema = z
    .record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/), z.union([
    z.string().max(PLATFORM_ERROR_LIMITS.contextString),
    z.number().finite(),
    z.boolean(),
    z.null(),
]))
    .refine((value) => Object.keys(value).length <= PLATFORM_ERROR_LIMITS.contextKeys, `At most ${PLATFORM_ERROR_LIMITS.contextKeys} context fields are accepted`);
export const platformErrorClientSchema = z
    .object({
    name: shortText(100),
    version: shortText(100).optional(),
    platform: shortText(100).optional(),
    arch: shortText(100).optional(),
    runtime: shortText(100).optional(),
})
    .strict();
const reportFields = {
    occurredAt: z.iso.datetime({ offset: true }).optional(),
    message: z.string().max(PLATFORM_ERROR_LIMITS.message),
    errorName: shortText(120).nullable().optional(),
    errorCode: shortText(120).nullable().optional(),
    stack: shortText(PLATFORM_ERROR_LIMITS.stack).nullable().optional(),
    requestId: requestIdSchema.nullable().optional(),
    operationId: z.uuid().nullable().optional(),
    httpStatus: z.number().int().min(0).max(599).nullable().optional(),
    appId: z.uuid().nullable().optional(),
    release: shortText(80).nullable().optional(),
    route: shortText(PLATFORM_ERROR_LIMITS.route).nullable().optional(),
    url: shortText(PLATFORM_ERROR_LIMITS.url).nullable().optional(),
    client: platformErrorClientSchema.optional(),
    context: platformErrorContextSchema.optional(),
};
/** One self-reported client error. The server attributes the reporter from its credential. */
export const platformErrorReportSchema = z
    .object({
    source: z.enum(PLATFORM_ERROR_CLIENT_SOURCES),
    kind: z.enum(PLATFORM_ERROR_CLIENT_KINDS),
    ...reportFields,
})
    .strict();
export const platformErrorReportBatchSchema = z
    .object({
    reports: z
        .array(platformErrorReportSchema)
        .min(1)
        .max(PLATFORM_ERROR_LIMITS.clientReports),
})
    .strict();
export const platformErrorReportAcceptedSchema = z.object({
    accepted: z.number().int().nonnegative(),
    references: z.array(z.uuid()),
});
/**
 * An event from a trusted platform service (for example the Agent service)
 * that already resolved who and what it concerns.
 */
export const platformErrorEventSchema = z
    .object({
    source: platformErrorSourceSchema,
    kind: platformErrorKindSchema,
    service: shortText(64).optional(),
    severity: platformIssueSeveritySchema.optional(),
    title: shortText(PLATFORM_ERROR_LIMITS.title).optional(),
    userId: z.uuid().nullable().optional(),
    runId: z.uuid().nullable().optional(),
    threadId: z.uuid().nullable().optional(),
    actorType: shortText(40).nullable().optional(),
    userAgent: shortText(300).nullable().optional(),
    httpMethod: shortText(10).nullable().optional(),
    /** Stable grouping inputs chosen by the reporting service. */
    fingerprint: z.array(shortText(200)).min(1).max(8).optional(),
    /** Retries of the same occurrence are recorded once. */
    dedupeKey: z.string().regex(/^[A-Za-z0-9._:-]{1,200}$/).nullable().optional(),
    ...reportFields,
})
    .strict();
export const platformErrorEventBatchSchema = z
    .object({
    events: z
        .array(platformErrorEventSchema)
        .min(1)
        .max(PLATFORM_ERROR_LIMITS.internalEvents),
})
    .strict();
const bearerPattern = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const openCloudTokenPattern = /\boc_[A-Za-z0-9_-]{12,}/g;
const jwtPattern = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g;
const providerKeyPattern = /\b(?:sk|pk|rk)-[A-Za-z0-9_-]{16,}/g;
const secretAssignmentPattern = /\b(password|passwd|secret|token|api[_-]?key|access[_-]?key|client[_-]?secret|authorization|cookie)(["']?\s*[:=]\s*["']?)[^\s"'&,;]+/gi;
/** Removes credentials from free text before it is stored or sent. */
export function redactPlatformErrorText(value) {
    return value
        .replace(bearerPattern, "Bearer [REDACTED]")
        .replace(jwtPattern, "[REDACTED_JWT]")
        .replace(openCloudTokenPattern, "[REDACTED]")
        .replace(providerKeyPattern, "[REDACTED_KEY]")
        .replace(secretAssignmentPattern, "$1$2[REDACTED]");
}
const userFailurePattern = /(allowance|budget|trial|credit|subscription|_disabled$|^agent_runs_disabled$|capacity_exhausted|queue_capacity_exceeded|plan_limit|cancel|user_)/i;
const providerFailurePattern = /(^|_)(provider|rate_limit|usage_limit|authentication|quota)(_|$)|^codex_provider_|^opencode2_provider_/i;
// A model that ends a recovery turn without its final answer is a model outcome.
const modelFailurePattern = /(tool_failed|turn_failed|refus|gate_failed|agent_task_deadline|max_turns|context_length|reconciliation_incomplete)/i;
/** Classifies a terminal Agent run error class. Unknown classes are platform faults. */
export function classifyAgentRunFailure(errorClass) {
    const value = errorClass?.trim() ?? "";
    if (!value)
        return "platform";
    if (userFailurePattern.test(value))
        return "user";
    if (providerFailurePattern.test(value))
        return "provider";
    if (modelFailurePattern.test(value))
        return "model";
    return "platform";
}
const noisyMessages = [
    /^ResizeObserver loop/i,
    /^Script error\.?$/i,
    /^Network request failed$/i,
];
// Leaving a page cuts off its requests, and browsers fail them like lost
// answers: Firefox and Safari as soon as the navigation starts, Chromium during
// `pagehide`. A page that is left takes its pending reports with it.
const LOST_ANSWER_DELAY_MS = 10_000;
function truncate(value, max) {
    return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
function errorParts(error) {
    if (error instanceof Error) {
        return { name: error.name || "Error", message: error.message || String(error), stack: error.stack ?? null };
    }
    if (typeof error === "string")
        return { name: null, message: error, stack: null };
    if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
        return { name: null, message: error.message, stack: null };
    }
    try {
        return { name: null, message: JSON.stringify(error) ?? String(error), stack: null };
    }
    catch {
        return { name: null, message: String(error), stack: null };
    }
}
function defaultSend(endpoint) {
    return (body) => {
        try {
            if (typeof fetch === "function") {
                void fetch(endpoint, {
                    method: "POST",
                    body,
                    headers: { "content-type": "application/json" },
                    credentials: "same-origin",
                    keepalive: true,
                }).catch(() => undefined);
                return;
            }
            if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
                navigator.sendBeacon(endpoint, new Blob([body], { type: "application/json" }));
            }
        }
        catch {
            // Reporting must never disturb the page.
        }
    };
}
/** A framework-free browser reporter for first-party OpenCloud clients. */
export function createPlatformErrorReporter(options) {
    const send = options.send ?? defaultSend(options.endpoint);
    const maxReports = options.maxReports ?? 10;
    const seen = new Set();
    let sent = 0;
    let installed = null;
    let pageHidden = false;
    let hiddenAt = Number.NEGATIVE_INFINITY;
    function offline() {
        try {
            return typeof navigator !== "undefined" && navigator.onLine === false;
        }
        catch {
            return false;
        }
    }
    function currentPath() {
        try {
            return typeof location === "undefined" ? null : location.pathname;
        }
        catch {
            return null;
        }
    }
    function report(error, details = {}) {
        try {
            const parts = errorParts(error);
            const message = redactPlatformErrorText(details.message ?? parts.message).trim();
            if (!message || noisyMessages.some((pattern) => pattern.test(message)))
                return;
            if (parts.stack && /(chrome|moz|safari-web)-extension:\/\//.test(parts.stack))
                return;
            const kind = details.kind ?? "client_error";
            const key = `${kind}|${parts.name ?? ""}|${message}|${parts.stack?.split("\n")[1]?.trim() ?? ""}`;
            if (seen.has(key) || sent >= maxReports)
                return;
            seen.add(key);
            sent += 1;
            const route = details.route ?? options.route?.() ?? currentPath();
            const payload = {
                source: options.source,
                kind,
                occurredAt: new Date().toISOString(),
                message: truncate(message, PLATFORM_ERROR_LIMITS.message),
                errorName: parts.name ? truncate(parts.name, 120) : null,
                errorCode: details.errorCode ? truncate(details.errorCode, 120) : null,
                stack: parts.stack ? truncate(redactPlatformErrorText(parts.stack), PLATFORM_ERROR_LIMITS.stack) : null,
                requestId: details.requestId && /^[A-Za-z0-9._:-]{1,128}$/.test(details.requestId) ? details.requestId : null,
                httpStatus: details.httpStatus ?? null,
                appId: details.appId ?? null,
                release: options.release ? truncate(options.release, 80) : null,
                route: route ? truncate(route, PLATFORM_ERROR_LIMITS.route) : null,
                url: currentPath(),
                ...(details.context ? { context: sanitizeContext(details.context) } : {}),
            };
            send(JSON.stringify({ reports: [payload] }));
        }
        catch {
            // Reporting must never disturb the page.
        }
    }
    function reactHandler(kind) {
        return (error, info) => {
            report(error, {
                kind: "client_error",
                context: {
                    reactBoundary: kind,
                    ...(info?.componentStack
                        ? { componentStack: truncate(info.componentStack.trim(), PLATFORM_ERROR_LIMITS.contextString) }
                        : {}),
                },
            });
        };
    }
    return {
        report,
        reportApiFailure(failure) {
            if (failure.status !== 0 && failure.status < 500)
                return;
            const reportFailure = () => report(new Error(failure.status === 0
                ? `No response from ${failure.method ?? "GET"} ${failure.path ?? "API"}`
                : `${failure.method ?? "GET"} ${failure.path ?? "API"} answered ${failure.status}`), {
                kind: "api_error",
                httpStatus: failure.status,
                errorCode: failure.code ?? null,
                requestId: failure.requestId ?? null,
                context: {
                    ...(failure.method ? { method: failure.method } : {}),
                    ...(failure.path ? { apiPath: truncate(failure.path.split("?")[0] ?? "", 300) } : {}),
                },
            });
            if (failure.status !== 0) {
                reportFailure();
                return;
            }
            if (pageHidden)
                return;
            const lostAt = Date.now();
            setTimeout(() => {
                // Still open, never hidden since (even if back from the back-forward
                // cache) and online: the answer went missing somewhere else.
                if (pageHidden || hiddenAt >= lostAt || offline())
                    return;
                reportFailure();
            }, LOST_ANSWER_DELAY_MS);
        },
        reactRootOptions() {
            return { onCaughtError: reactHandler("caught"), onUncaughtError: reactHandler("uncaught") };
        },
        install() {
            if (installed)
                return installed;
            if (typeof window === "undefined")
                return () => undefined;
            const onError = (event) => {
                if (!event.error && !event.message)
                    return;
                report(event.error ?? event.message);
            };
            const onRejection = (event) => {
                const reason = event.reason;
                report(reason instanceof Error ? reason : new Error(errorParts(reason).message), {
                    context: { unhandledRejection: true },
                });
            };
            const onPreloadError = (event) => {
                const payload = event.payload;
                report(payload instanceof Error ? payload : new Error("Application code failed to load"), {
                    kind: "chunk_load_failed",
                });
                if (!options.reloadOnChunkError)
                    return;
                try {
                    const key = `oc:chunk-reload:${options.release ?? "unknown"}`;
                    if (sessionStorage.getItem(key))
                        return;
                    sessionStorage.setItem(key, "1");
                    event.preventDefault();
                    location.reload();
                }
                catch {
                    // Without storage the page keeps its normal error handling.
                }
            };
            // Chromium fails a leaving page's requests during `pagehide`. A page
            // restored from the back-forward cache is open again.
            const onPageHide = () => {
                pageHidden = true;
                hiddenAt = Date.now();
            };
            const onPageShow = () => {
                pageHidden = false;
            };
            window.addEventListener("error", onError);
            window.addEventListener("unhandledrejection", onRejection);
            window.addEventListener("vite:preloadError", onPreloadError);
            window.addEventListener("pagehide", onPageHide);
            window.addEventListener("pageshow", onPageShow);
            installed = () => {
                window.removeEventListener("error", onError);
                window.removeEventListener("unhandledrejection", onRejection);
                window.removeEventListener("vite:preloadError", onPreloadError);
                window.removeEventListener("pagehide", onPageHide);
                window.removeEventListener("pageshow", onPageShow);
                installed = null;
            };
            return installed;
        },
    };
}
function sanitizeContext(context) {
    const entries = Object.entries(context)
        .filter(([key]) => /^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(key))
        .slice(0, PLATFORM_ERROR_LIMITS.contextKeys)
        .map(([key, value]) => [
        key,
        typeof value === "string"
            ? truncate(redactPlatformErrorText(value), PLATFORM_ERROR_LIMITS.contextString)
            : typeof value === "number" && !Number.isFinite(value)
                ? null
                : value,
    ]);
    return Object.fromEntries(entries);
}
//# sourceMappingURL=platform-errors.js.map